import { motion } from "framer-motion";
import { Css } from "~generated/css";
import { useTestIds } from "~utils";

type ToggleComponentProps = {
  offLabel: string;
  onLabel: string;
  value: boolean;
  onClick: () => void;
  // disabled?: boolean;
};

/** While it's generally preferable to avoid this type of math for sizing elements (vs just using CSS tools like Grid or Flex)
 * the animated element requires static width calculations to ensure the animation can appear to float smoothly between selections.
 * Since we can set a fixed font size locally to this component, we can avoid having to use DOM sizing calculations via a `useLayoutEffect` hook
 * and instead just rely on a bit of simple math to do the rest.
 */
const fontSizePx = 10;
const widthMultiplier = 0.8;
const containerPaddingPx = 2;

export const ToggleComponent = (props: ToggleComponentProps) => {
  const { onLabel, offLabel, value, onClick } = props;
  const tid = useTestIds(props, "toggle");

  // Find which of the two labels is the largest, then choose the largest of the two as the shared label width for both sides
  // Note if we want to support different sizes for each label, the animated background element will also need to update
  // its width and offset accordingly (and deal with animating the width in a way that looks natural)
  const offLabelChars = offLabel.split("").length;
  const onLabelChars = onLabel.split("").length;
  const maxChars = Math.max(offLabelChars, onLabelChars);
  const labelWidth = maxChars * (fontSizePx * widthMultiplier);
  const totalWidth = labelWidth * 2 + containerPaddingPx * 2;

  return (
    <div
      onClick={onClick}
      css={
        Css.cursorPointer.df.relative
          .wPx(totalWidth)
          .hPx(25)
          .pPx(containerPaddingPx)
          .bshBasic.br16.bgWhite.fsPx(fontSizePx).$
      }
    >
      <motion.div
        initial={{ x: 0 }}
        animate={{ x: value ? labelWidth : 0 }}
        css={Css.df.z0.absolute.hPx(21).wPx(labelWidth).br16.bgGray800.$}
      />
      <div css={Css.df.w100.relative.z1.jcsb.aic.$} {...tid}>
        <span
          css={Css.wPx(labelWidth).tac.t10.add("transition", "0.2s").if(!value).white.else.gray700.$}
          {...tid.offLabel}
        >
          {offLabel}
        </span>
        <span
          css={Css.wPx(labelWidth).tac.t10.add("transition", "0.2s").if(value).white.else.gray700.$}
          {...tid.onLabel}
        >
          {onLabel}
        </span>
      </div>
    </div>
  );
};
